import { getSupabase, getSupabaseAsync } from '../../shared/functions/supabaseClient.js'
import { viewedEmail, viewedPerson, findPerson, isSelf } from '../../shared/functions/peopleUtils.js'
import { changeGlobalTheme as _changeGlobalTheme } from 'smbls'

export { canvasOrigin, canvasUrl } from '../../shared/functions/canvasUrl.js'

// Expose smbls' changeGlobalTheme as a callable function so MeMenu's
// theme-cycle item (and any future theme switcher) can flip data-theme
// via `el.call('changeGlobalTheme', 'light' | 'dark' | 'auto')` rather
// than mutating the attribute directly. Required because the workspace
// shell's wireGlobalListeners doesn't run for the standalone wp app.
export const changeGlobalTheme = (...args) => _changeGlobalTheme(...args)

export const sb = getSupabase
export { getSupabaseAsync }

// Profile-style pages render for `?u=email` when set; fall back to a `{}`
// placeholder so legacy templates reading `findMe(s).name` don't crash on undefined.
export const findMe = (s, { allowQuery = true } = {}) => {
  return viewedPerson(s, { allowQuery }) || findPerson(s && s.root, s && s.root && s.root.currentUserEmail) || {}
}

const PALETTE = ['blue', 'purple', 'pink', 'cerulean', 'green', 'orange', 'indigo']
export const colorFromEmail = (email) => {
  const k = (email || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  return PALETTE[k % PALETTE.length]
}

export const initials = (name) => (name || '')
  .split(/\s+/).filter(Boolean).slice(0, 2)
  .map(p => p[0]).join('').toUpperCase()

const _rolesByName = (s) => {
  const list = s && s.root && s.root.roles && s.root.roles.available
  if (!list || !list.length) return {}
  const map = {}
  for (const r of list) map[r.name] = r
  return map
}

export const allRoles = (s) =>
  (s && s.root && s.root.roles && s.root.roles.available) || []

export const roleOf = (s, userId) =>
  (s && s.root && s.root.roles && s.root.roles.byUser && s.root.roles.byUser[userId]) || null

export const currentRole = (s) => roleOf(s, s && s.root && s.root.currentUserId)

export const roleMeta = (s, role) => _rolesByName(s)[role] || null

export const roleColor = (s, role) => roleMeta(s, role)?.color || 'gray'
export const roleLabel = (s, role) => roleMeta(s, role)?.label || (role ? role.replace(/^./, c => c.toUpperCase()) : 'Member')
export const roleIsCore = (s, role) => !!roleMeta(s, role)?.is_core

export const isCoreMember = (s) => {
  const role = currentRole(s)
  if (!role) return false
  return roleIsCore(s, role)
}

const _fmtCache = new Map()
const _fmt = (locale, options) => {
  const k = locale + JSON.stringify(options)
  if (!_fmtCache.has(k)) _fmtCache.set(k, new Intl.DateTimeFormat(locale, options))
  return _fmtCache.get(k)
}

export const formatTime = (ts, tz) => {
  if (!ts) return ''
  try {
    return _fmt('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz, hour12: true })
      .format(ts).toLowerCase().replace(' ', '')
  } catch {
    return _fmt('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).format(ts)
  }
}

export const formatLongDay = (ts) =>
  _fmt('en-US', { day: 'numeric', month: 'long', weekday: 'long' }).format(ts)

export const formatMonthShort = (ts) =>
  _fmt('en', { month: 'short' }).format(ts)

// Re-export all shared-gov functions so they're available via el.call()
export * from './aiAssistant.js'
export * from '../../shared/functions/supabaseClient.js'
export * from '../../shared/functions/supabaseConfig.js'
export * from './supabaseRow.js'
export * from './auth.js'
export * from './roleHelpers.js'
export * from '../../shared/functions/peopleUtils.js'
export * from './pageBoilerplate.js'
export * from '../../shared/functions/dateUtils.js'
export * from './githubColors.js'
export * from './formatUtils.js'
export * from './setNavbarTooltipPosition.js'
export * from './peopleGroups.js'
export * from './resourceLinks.js'
export * from './tierGate.js'
export * from './teams.js'

export * from './calendar/index.js'
export * from './tickets/index.js'
export * from './documents/index.js'
export * from './meet/index.js'
export * from './notifications/index.js'
export * from './admin/index.js'
export * from './search/index.js'

// startOAuthFlow — stub. Opens the provider OAuth dialog, persists
// returned tokens to Vault via `integration-admin` upsert. Today throws
// a clear "not wired" message so the `MarketplaceOAuthConnectButton`
// click surfaces a visible error rather than silently failing. DOMAIN's
// first OAuth2 provider (Cloudflare registrar) triggers the full
// implementation; at that point this function gets:
//   1. Build provider OAuth URL + state param encoding {kind, scope, slug}
//   2. window.open in popup / redirect
//   3. Callback handler resolves code → exchange for tokens → Vault store
// Per MARKETPLACE tick-4 REQUEST; scope: workspace/packages/workspace/functions/.
export const startOAuthFlow = (kind, { scopeType, scopeId, slug } = {}) => {
  console.warn(
    `[startOAuthFlow] stub — kind=${kind} scope=${scopeType}:${scopeId ?? 'null'} slug=${slug ?? 'default'}`
  )
  throw new Error(
    `OAuth flow not wired yet for ${kind} — coming in follow-up once DOMAIN's first OAuth2 provider (Cloudflare registrar) ships.`
  )
}

// closeModal — called by ModalHeader Icons > Close button.
// Walks up the element tree to find the modal's stateKey, then clears it.
// Guards against ancestors that have non-string stateKey or non-object
// extraClose (e.g. reactive factory functions that haven't been resolved).
export const closeModal = function () {
  const root = this?.state?.root
  if (!root?.update) return
  let m = this
  while (m) {
    const sk = typeof m.stateKey === 'string' ? m.stateKey : null
    if (sk) {
      const extra =
        m.extraClose && typeof m.extraClose === 'object' && !Array.isArray(m.extraClose)
          ? m.extraClose
          : null
      root.update(extra ? { [sk]: null, ...extra } : { [sk]: null })
      return
    }
    m = m.parent
  }
}
